/* global __initial_auth_token */

import React, { useState, useEffect, createContext, useContext, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithCustomToken,
  // signInAnonymously,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  onSnapshot,
  collection,
  query,
  where,
  setDoc
} from 'firebase/firestore';
import { getFunctions } from "firebase/functions"; // <-- ADD THIS LINE
import { getStorage } from 'firebase/storage';
// import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import ForgotPasswordPage from './components/ForgotPasswordPage';


// Import components
import RegisterPage from './components/RegisterPage';
import MyProfilePage from './components/MyProfilePage';
import MyInterestsPage from './components/MyInterestsPage';
import DiscoverPage from './components/DiscoverPage';
import ShortlistPage from './components/ShortlistPage';
import BlockedUsersPage from './components/BlockedUsersPage';
import UserGalleryPage from './components/UserGalleryPage';
import ProfileView from './components/ProfileView';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import HomePage from './components/HomePage';
import PaymentIntegration from './components/PaymentIntegration';
import PaymentSuccessPage from './components/PaymentSuccessPage';
import PaymentCanceledPage from './components/PaymentCanceledPage';
import SubscriptionStatusCard from './components/SubscriptionStatusCard';
import PhoneVerification from './components/PhoneVerification';

// Firebase Configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: "AIzaSyD7q3SMmz9aUOZOgxDMOXqQoOZReC0LpFc",
  authDomain: "mymatrimonyapp-c96ce.firebaseapp.com",
  projectId: "mymatrimonyapp-c96ce",
  storageBucket: "mymatrimonyapp-c96ce.firebasestorage.app",
  messagingSenderId: "36647802031",
  appId: "1:36647802031:web:2d209e71f545947d42b05a",
  measurementId: "G-5L2MB2K0HY"
};

const initialAuthToken = typeof __initial_auth_token !== 'undefined' ? __initial_auth_token : null;

const app = initializeApp(firebaseConfig);

// Initialize App Check with reCAPTCHA for phone auth security
// initializeAppCheck(app, {
//  provider: new ReCaptchaV3Provider('6LfYzvIrAAAAAOtdY1dpO2YDHaAak8b-5W6pmyIs'),
//  isTokenAutoRefreshEnabled: true
// });
const auth = getAuth(app);
export const db = getFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(app); // <-- ADD THIS LINE

export const AuthContext = createContext(null);

// Custom hook to use the Auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
  const setupFirebase = async () => {
    try {
      if (initialAuthToken) {
        await signInWithCustomToken(auth, initialAuthToken);
      }
      // ✅ Removed anonymous sign-in - users must sign in with email/password
    } catch (error) {
      console.error("Firebase initial sign-in failed:", error);
    }

      const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
        setUser(currentUser);
        if (currentUser) {
          setUserId(currentUser.uid);
          const userDocRef = doc(db, 'users', currentUser.uid);
          try {
            onSnapshot(userDocRef, (docSnap) => {
              if (docSnap.exists()) {
                setUserProfile(docSnap.data());
              } else {
                const defaultProfile = {
                  email: currentUser.email,
                  role: 'user',
                  uniqueUserId: '',
                  createdAt: new Date(),
                  isProfileApproved: false,
                  firstName: '',
                  lastName: '',
                  profileStatus: 'incomplete',
                  profileViews: 0,
                  newMatches: 0,
                  conversations: 0,
                  interestsReceived: 0,
                  shortlistedCount: 0,
                  membershipType: 'free',
                };
                setUserProfile(defaultProfile);
                setDoc(userDocRef, defaultProfile, { merge: true }).catch(err => console.error("Error setting default user profile:", err));
              }
            }, (error) => {
              console.error("Error real-time fetching user document:", error);
              setUserProfile(null);
            });
          } catch (firestoreError) {
            console.error("Error fetching or creating user document in AuthProvider:", firestoreError);
            setUserProfile(null);
          }
        } else {
          setUserProfile(null);
          setUserId(null);
        }
        setIsAuthReady(true);
      });

      return () => unsubscribe();
    };

    setupFirebase();
  }, []);

  return (
  <AuthContext.Provider value={{ user, userProfile, userId, isAuthReady, auth, db, storage }}>
    {children}
  </AuthContext.Provider>
);
}

// Themed Message Box Component
function MessageBox({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full text-center">
        <p className="text-lg font-semibold text-gray-800 mb-6">{message}</p>
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-lg shadow-md text-white font-semibold transition-all duration-300 hover:shadow-lg"
          style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          OK
        </button>
      </div>
    </div>
  );
}

// Themed Confirmation Modal
export function ConfirmationModal({ message, onConfirm, onCancel }) {
  if (!message) return null;
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full text-center">
        <p className="text-lg font-semibold text-gray-800 mb-6">{message}</p>
        <div className="flex justify-center space-x-4">
          <button
            onClick={onConfirm}
            className="px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition duration-200 shadow-md"
          >
            Confirm
          </button>
          <button
            onClick={onCancel}
            className="px-6 py-3 bg-gray-300 text-gray-800 rounded-lg font-semibold hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition duration-200 shadow-md"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Premium Upgrade Page Component
export function PremiumUpgradePage() {
  const navigate = useNavigate();
  // const { userProfile } = useContext(AuthContext);  // ← Remove or comment out
  return (
    <div className="container mx-auto px-6 py-12">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="mb-6 text-gray-600 hover:text-gray-800 flex items-center gap-2 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Dashboard
        </button>

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="p-12 text-center" style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}>
            <h1 className="text-5xl font-bold text-white mb-4">Upgrade to Premium</h1>
            <p className="text-xl text-white opacity-90">Unlock unlimited possibilities for your journey</p>
          </div>

          <div className="p-12">
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Free Plan */}
              <div className="border-2 border-gray-200 rounded-2xl p-8">
                <h3 className="text-2xl font-bold mb-4" style={{ color: 'hsl(221, 45%, 18%)' }}>Free Membership</h3>
                <p className="text-4xl font-bold mb-6" style={{ color: 'hsl(221, 45%, 18%)' }}>$0<span className="text-lg font-normal text-gray-600">/month</span></p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-700">Basic profile creation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-700">Browse profiles</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-700">Send limited interests</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                    <span className="text-gray-400 line-through">No shortlist feature</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                    <span className="text-gray-400 line-through">Limited profile visibility</span>
                  </li>
                </ul>
              </div>

              {/* Premium Plan */}
              <div className="border-4 rounded-2xl p-8 relative" style={{ borderColor: 'hsl(43, 74%, 52%)' }}>
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-white font-bold text-sm" style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}>
                  RECOMMENDED
                </div>
                <h3 className="text-2xl font-bold mb-4" style={{ color: 'hsl(43, 74%, 52%)' }}>Premium Membership</h3>
                <p className="text-4xl font-bold mb-6" style={{ color: 'hsl(221, 45%, 18%)' }}>$49<span className="text-lg font-normal text-gray-600">/month</span></p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-700 font-semibold">Everything in Free, plus:</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-700">Unlimited shortlist feature</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-700">Unlimited interests</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-700">Priority profile visibility</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-700">Advanced search filters</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-700">24/7 Premium support</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="text-center">
              <h3 className="text-3xl font-bold mb-6" style={{ color: 'hsl(221, 45%, 18%)' }}>Ready to Find Your Perfect Match?</h3>
              <p className="text-xl text-gray-600 mb-8">Join thousands of happy couples who found their life partner with Premium membership</p>
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-8 mb-8">
                <p className="text-lg text-gray-700 mb-4">
                  <strong>Note:</strong> This is a demo page. To implement actual payment processing, you'll need to integrate a payment gateway like Stripe, PayPal, or Razorpay.
                </p>
                <p className="text-gray-600">
                  Contact your development team to set up secure payment processing and subscription management.
                </p>
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-8 py-4 rounded-xl text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Continue to Payment (Coming Soon)
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Themed Login Component
function LoginPage() {
  const navigate = useNavigate();
  const { auth } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [showPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage('');
   try {
  await signInWithEmailAndPassword(auth, email, password);
  setMessage('Login successful!');
  setEmail('');
  setPassword('');
  navigate('/dashboard');

    } catch (error) {
  // Check for specific Firebase auth error codes
  if (error.code === 'auth/invalid-credential' ||
      error.code === 'auth/wrong-password' ||
      error.code === 'auth/user-not-found') {
    setMessage('Invalid email or password. Please try again.');
  } else if (error.code === 'auth/invalid-email') {
    setMessage('Please enter a valid email address.');
  } else if (error.code === 'auth/too-many-requests') {
    setMessage('Too many failed attempts. Please try again later.');
  } else {
    setMessage(`Login failed: ${error.message}`);
  }
  console.error("Login error:", error);
}
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4" style={{ background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)' }}>
      <div className="bg-white p-10 rounded-3xl shadow-2xl w-full max-w-md" style={{ backdropFilter: 'blur(20px)', background: 'rgba(255, 255, 255, 0.95)' }}>
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>Welcome Back</h2>
          <p className="text-gray-600">Sign in to continue your divine journey</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-semibold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
              Email Address
            </label>
            <input
              type="email"
              id="email"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition duration-200 outline-none"
              placeholder="your@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-semibold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
              Password
            </label>
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition duration-200 outline-none"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>


<div className="text-right">
  <button
    type="button"
    onClick={() => navigate('/forgot-password')}
    className="text-sm font-semibold hover:underline transition duration-200"
    style={{ color: 'hsl(43, 74%, 52%)' }}
  >
    Forgot Password?
  </button>
</div>

          <button
            type="submit"
            className="w-full py-3 rounded-xl text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            Sign In
          </button>
        </form>



        <div className="mt-8 text-center space-y-4">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <button
              onClick={() => navigate('/register')}
              className="font-semibold hover:underline transition duration-200"
              style={{ color: 'hsl(43, 74%, 52%)' }}
            >
              Register here
            </button>
          </p>
          <button
            onClick={handleBackToHome}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium hover:underline transition duration-200"
          >
            ← Back to Home
          </button>
        </div>

        <MessageBox message={message} onClose={() => setMessage('')} />
      </div>
    </div>
  );
}

// Themed Navigation
function AppNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, auth } = useContext(AuthContext);
  const [message, setMessage] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    setMessage('');
    try {
      await signOut(auth);
      setMessage('Logged out successfully!');
      navigate('/');
    } catch (error) {
      setMessage(`Logout failed: ${error.message}`);
      console.error("Logout error:", error);
    }
  };

  const getCurrentView = () => {
    const path = location.pathname;
     if (path === '/') return 'home';
    if (path === '/dashboard') return 'dashboard';
    if (path === '/discover') return 'discover';
    if (path === '/my-profile') return 'myProfile';
    if (path === '/my-interests') return 'myInterest';
    if (path === '/shortlist') return 'shortlist';
    if (path === '/gallery') return 'gallery';
    if (path === '/blocked') return 'blocked';
    if (path === '/premium-upgrade') return 'premium';
    if (path.startsWith('/profile/')) return 'profile';
    return 'dashboard';
  };

  const currentView = getCurrentView();

  return (
    <header className="fixed top-0 w-full z-50 transition-all duration-300 shadow-md border-b border-gray-200 bg-white">
      {message && <MessageBox message={message} onClose={() => setMessage('')} />}

      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-24">
          <div className="flex items-center gap-3">
            <img src="/upscalemedia-transformed.jpeg" alt="Divine Covenant" className="h-24 w-auto" />
          </div>

          {user && userProfile && userProfile.role !== 'super_admin' && (
            <nav className="hidden md:flex items-center gap-8">
              {[
                { key: 'home', path: '/', label: 'Home' },
  { key: 'dashboard', path: '/dashboard', label: 'Dashboard' },
  { key: 'discover', path: '/discover', label: 'Discover' },
  { key: 'myProfile', path: '/my-profile', label: 'My Profile' },
  { key: 'myInterests', path: '/my-interests', label: 'My Interests' },
  { key: 'gallery', path: '/gallery', label: 'Gallery' },
  { key: 'blocked', path: '/blocked', label: 'Blocked' },
                ...(userProfile?.membershipType === 'premium' ? [
                  { key: 'shortlist', path: '/shortlist', label: 'Shortlist' }
                ] : [])
              ].map((item) => (
                <button
                  key={item.key}
                  onClick={() => navigate(item.path)}
                  className={`relative text-lg transition-colors ${
                    currentView === item.key ? 'font-semibold' : ''
                  }`}
                  style={{
                    background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(43, 74%, 52%) 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  {item.label}
                  {currentView === item.key && (
                    <div className="absolute -bottom-1 left-0 w-full h-0.5 rounded-full"
                         style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }} />
                  )}
                </button>
              ))}
            </nav>
          )}

          {user && userProfile && (
            <div className="hidden md:flex items-center gap-3">
              <span className="font-medium" style={{ color: 'hsl(221, 45%, 18%)' }}>
  {userProfile?.firstName?.trim() || user?.displayName || user?.email?.split('@')[0] || 'User'}
                {userProfile.role === 'super_admin' && (
                  <span className="ml-1 capitalize font-bold">({userProfile.role})</span>
                )}
              </span>
              <button
                onClick={handleLogout}
                className="px-6 py-2 bg-white text-gray-900 rounded-lg font-semibold border-2 border-yellow-500 hover:bg-yellow-50 transition duration-200 shadow-md"
              >
                Logout
              </button>
            </div>
          )}

          {user && (
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 transition-colors"
              style={{ color: '#D4A747' }}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

// Updated User Dashboard
function UserDashboard() {
  const navigate = useNavigate();
  const { user, userId, userProfile, isAuthReady, db } = useContext(AuthContext);
  const [message, setMessage] = useState('');
  const [shortlistedCount, setShortlistedCount] = useState(0);

  useEffect(() => {
    let unsubscribeShortlists;

    // Only fetch shortlisted count for premium users
    if (isAuthReady && userId && userProfile?.membershipType === 'premium') {
      const shortlistsRef = collection(db, 'shortlists');
      const q = query(shortlistsRef, where('likerId', '==', userId));

      unsubscribeShortlists = onSnapshot(q, (snapshot) => {
        setShortlistedCount(snapshot.size);
      }, (error) => {
        console.error("Error fetching shortlisted profiles count:", error);
        setMessage(`Error loading shortlisted count: ${error.message}`);
      });
    } else {
      // Reset count for free users
      setShortlistedCount(0);
    }

    return () => {
      if (unsubscribeShortlists) unsubscribeShortlists();
    };
  }, [userId, db, isAuthReady, userProfile?.membershipType]);

  const isProfileComplete = userProfile?.profileStatus === 'complete';

  const profileCompletionPercentage = useMemo(() => {
    if (!userProfile) return 0;
    if (userProfile.profileStatus === 'complete') return 100;

    let completedFields = 0;
    const totalFields = 5;

    if (userProfile.firstName) completedFields++;
    if (userProfile.lastName) completedFields++;
    if (userProfile.gender) completedFields++;
    if (userProfile.dob) completedFields++;
    if (userProfile.phoneNumber) completedFields++;

    return Math.min(100, Math.round((completedFields / totalFields) * 100));
  }, [userProfile]);

  if (!isAuthReady || !userProfile) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)' }}>
        <p className="text-xl text-white">Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 pt-0 pb-6">
      {message && <MessageBox message={message} onClose={() => setMessage('')} />}
      <div className="bg-white p-8 rounded-2xl shadow-xl mb-8 mt-0 flex flex-col md:flex-row items-center justify-between transform hover:shadow-2xl transition-all duration-300"></div>

      {/* Welcome Section */}
      <div className="bg-white p-8 rounded-2xl shadow-xl mb-8 mt-2 flex flex-col md:flex-row items-center justify-between transform hover:shadow-2xl transition-all duration-300">
        <div className="flex items-center space-x-4 mb-6 md:mb-0">
          <div className="w-20 h-20 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-lg"
               style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}>
            {userProfile.firstName ? userProfile.firstName.charAt(0).toUpperCase() : (user?.email ? user.email.charAt(0).toUpperCase() : 'U')}
          </div>
          <div>
            <h2 className="text-3xl font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>
  Welcome Back!
</h2>
{userProfile?.uniqueUserId ? (
  <p className="text-gray-600 mt-1">
    <span className="font-semibold" style={{ color: 'hsl(43, 74%, 52%)' }}>
      Member ID: {userProfile.uniqueUserId}
    </span>
  </p>
) : null}
<p className="text-gray-600 mt-1">Here's your activity summary.</p>
          </div>
        </div>
        <div className="w-full md:w-auto flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6">
          {isProfileComplete ? (
            <span className="font-bold text-xl flex items-center gap-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
              Profile Complete!
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
          ) : (
            <>
              <div className="w-full md:w-64 bg-gray-200 rounded-full h-4 shadow-inner">
                <div
                  className="h-4 rounded-full transition-all duration-500 shadow-md"
                  style={{
                    width: `${profileCompletionPercentage}%`,
                    background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)'
                  }}
                ></div>
              </div>
              <span className="font-bold text-lg" style={{ color: 'hsl(43, 74%, 52%)' }}>{profileCompletionPercentage}% Complete</span>
            </>
          )}
          <button
  onClick={() => navigate('/my-profile')}
  className="px-6 py-3 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300"
  style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
>
  Edit Profile
</button>
        </div>
      </div>


      {/* Subscription Status Section */}
      <SubscriptionStatusCard />
      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>Profile Views</h3>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md"
                 style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
          </div>
          <p className="text-4xl font-bold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>{userProfile.profileViews || 0}</p>
          <p className="text-sm text-gray-600">total views</p>
        </div>


        <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>New Matches</h3>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md"
                 style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
          </div>
          <p className="text-4xl font-bold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>{userProfile.newMatches || 0}</p>
          <p className="text-sm text-gray-600">mutually interested</p>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>Interests Received</h3>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md"
                 style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.653-.146-1.282-.42-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.653.146-1.282.42-1.857m0 0a2 2 0 10-3.322-1.857A3 3 0 017 8.5c0 .546.128 1.077.368 1.565m0 0A2 2 0 1015.632 8.5a3 3 0 01-.368 1.565m0 0c-.274.575-.42 1.204-.42 1.857M12 10a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
          </div>
          <p className="text-4xl font-bold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>{userProfile.interestsReceived || 0}</p>
          <p className="text-sm text-gray-600">total received</p>
        </div>

        {/* ONLY show Shortlisted Profiles card for Premium users */}
        {userProfile?.membershipType === 'premium' && (
          <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>Shortlisted Profiles</h3>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md"
                   style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
            </div>
            <p className="text-4xl font-bold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>{shortlistedCount}</p>
            <p className="text-sm text-gray-600">you shortlisted</p>
          </div>
        )}
      </div>
    </div>
  );
}

function UpdatedRegisterPage() {
  const navigate = useNavigate();

  const handleBackToHome = () => {
    navigate('/');
  };

  const onLoginClick = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)' }}>
      <main className="flex-grow flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <RegisterPage onLoginClick={onLoginClick} />
          <p className="mt-4 text-center text-sm">
            <button
              onClick={handleBackToHome}
              className="font-medium text-yellow-400 hover:text-yellow-300 focus:outline-none focus:underline transition duration-200"
            >
              ← Back to Home
            </button>
          </p>
        </div>
      </main>
      <footer className="text-white text-center p-6" style={{ background: 'hsl(221, 55%, 12%)' }}>
        <p>&copy; {new Date().getFullYear()} Divine Covenant. All rights reserved.</p>
      </footer>
    </div>
  );
}

function MyProfileWrapper() {
  const navigate = useNavigate();
  return <MyProfilePage onBackToDashboard={() => navigate('/dashboard')} />;
}


function AppContent() {
  const { user, userProfile, isAuthReady } = useContext(AuthContext);

  if (!isAuthReady) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)' }}>
        <p className="text-xl text-white">Initializing application...</p>
      </div>
    );
  }

  if (user) {
    if (userProfile?.role === 'super_admin') {
      return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
          <AppNavigation />
          <main className="flex-grow">
            <SuperAdminDashboard />
          </main>
          <footer className="text-white text-center p-6" style={{ background: 'hsl(221, 55%, 12%)' }}>
            <p>&copy; {new Date().getFullYear()} Divine Covenant. All rights reserved.</p>
          </footer>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <AppNavigation />
        <main className="flex-grow p-4">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/dashboard" element={<UserDashboard />} />
            <Route path="/discover" element={<DiscoverPage />} />
            <Route path="/my-profile" element={<MyProfileWrapper />} />
            <Route path="/my-interests" element={<MyInterestsPage />} />
            <Route path="/shortlist" element={<ShortlistPage />} />
            <Route path="/gallery" element={<UserGalleryPage />} />
            <Route path="/blocked" element={<BlockedUsersPage />} />
            <Route path="/profile/:userId" element={<ProfileView />} />
           <Route path="/premium-upgrade" element={<PaymentIntegration />} />
           <Route path="/payment-success" element={<PaymentSuccessPage />} />
           <Route path="/payment-canceled" element={<PaymentCanceledPage />} />
           <Route path="/phone-verification" element={<PhoneVerification />} />
           <Route path="/forgot-password" element={<ForgotPasswordPage />} />
           <Route path="*" element={<UserDashboard />} />
           </Routes>
        </main>
        <footer className="text-white text-center p-6" style={{ background: 'hsl(221, 55%, 12%)' }}>
          <p>&copy; {new Date().getFullYear()} Divine Covenant. All rights reserved.</p>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<UpdatedRegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/premium-upgrade" element={<PaymentIntegration />} />
        <Route path="/payment-success" element={<PaymentSuccessPage />} />
        <Route path="/payment-canceled" element={<PaymentCanceledPage />} />
        <Route path="*" element={<HomePage />} />
      </Routes>
    </div>
  );
}

export function App() {
  useEffect(() => {
    const link = document.createElement('script');
    link.src = "https://cdn.tailwindcss.com";
    document.head.appendChild(link);

    const style = document.createElement('style');
    style.innerHTML = `
      @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

      body {
        font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
      }

      :root {
        --primary: 221 45% 18%;
        --primary-foreground: 0 0% 100%;
        --accent: 43 74% 52%;
        --accent-foreground: 0 0% 100%;
        --muted: 220 15% 96%;
        --muted-foreground: 221 25% 45%;
        --gradient-hero: linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%);
        --gradient-gold: linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%);
        --shadow-divine: 0 10px 30px -10px hsl(221, 45%, 18%, 0.3);
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export { functions };

export default function WrappedApp() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}